/**
 * P3-10: BonfireTab —— Bonfire tab 分组显示与组装根（迁移自
 * com.nuclearunicorn.game.ui.tab.BuildingsModern，js/buildings.js:3302-3528）。
 *
 * 结构逐一对齐（INVENTORY Part 1）：
 * - 根 div.tabInner.Bonfire（js/ui.js:448-457 的 tab 内容容器层级）；
 * - div.bldTopContainer：分组链接 <a>（activeGroup 加 activeTab class，
 *   whiteSpace:nowrap）+ 分隔 span " · "（i!=0 才有；display 随本组
 *   hasVisibleBldngs，buildings.js:3368-3386）；
 * - div.bldGroupContainer：激活组内容（buildings.js:3402）；
 * - twoRows（activeGroup=="all"||"iw"）时 table>tr>td×2（width:100%、
 *   cellpadding/cellspacing=0、td verticalAlign:top，core.js:2431-2444），
 *   可见按钮按索引奇偶分列（getElementContainer，core.js:2449-2459）；
 * - 核心按钮（gather/refine）仅在筛选组下渲染（i==0 分支，
 *   buildings.js:3418-3431）。
 *
 * 分组数据（buildings.js:3319-3352）：game.bld.buildingGroups 浅拷贝 + 头部
 * unshift 五个筛选组（all/available/allEnabled/togglable/[iw]）；组可见性 =
 * 组内任一建筑 unlocked，空组恒可见。activeGroup 存 game.bldTab.activeGroup
 * （旧版存 tab 实例，INVENTORY Part 2：会话内保留、不持久化），默认 "all"。
 *
 * 过滤语义（buildings.js:3461-3497）：available 跳过 resourceIsLimited、
 * allEnabled 跳过 !enabled、togglable 跳过 !togglable、iw 跳过 population 组、
 * 所有组跳过 !visible。**与旧版的求值时机差**：旧版仅 game.render() 时重算
 * 成员，本实现每 tick 重算（选择器驱动）；静态瞬间两者一致（INVENTORY
 * "成员资格求值时机"，CHECKLIST §8 声明）。
 *
 * 性能：筛选组（available/allEnabled/togglable）每 tick 对每个建筑各调一次
 * fetchModel 求过滤值（用一次性求值 controller，不进按钮的 modelRef）；
 * all/iw 只读 unlocked，不 fetchModel。按钮级模型仍由各自 useButtonModel
 * 每 tick 刷新（对齐旧 tab.update → children update，core.js:2631-2641）。
 *
 * 挂载：经 midPartition.registerMigratedTab("Bonfire", BonfireTab) 登记后由
 * MidPanel 渲染（P3-11 启用）；本文件不直接产生 DOM 挂载。
 */
import { Fragment, useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { BuildingBtn } from "./BuildingBtn";
import { GatherCatnipButton, RefineCatnipButton } from "./GatherRefine";
import type { ModernButtonController } from "./useButtonModel";

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

export interface BonfireGroupEntry {
  name: string;
  title: string;
  visible: boolean;
}

export interface BonfireButtonDesc {
  kind: "gather" | "refine" | "bld";
  name?: string;
  staging?: boolean;
}

export interface BonfireSnapshot {
  activeGroup: string;
  twoRows: boolean;
  groups: BonfireGroupEntry[];
  buttons: BonfireButtonDesc[];
}

/** 筛选组（renderActiveGroup 的 continue 白名单，buildings.js:3420-3424） */
const FILTER_GROUPS = ["all", "available", "allEnabled", "togglable", "iw"];

interface RawGroup {
  name: string;
  title: string;
  buildings: string[];
}

interface BldLike {
  buildingGroups: RawGroup[];
  get(name: string): { unlocked: boolean };
  getBuildingExt(name: string): {
    getMeta(): {
      label?: string;
      description?: string;
      stages?: unknown;
    };
  };
}

/** buildings.js:3319-3348：浅拷贝 buildingGroups + 头部筛选组（最终顺序 all 在前） */
export function buildGroups(game: GamePage): RawGroup[] {
  const bld = game.bld as BldLike;
  const groups: RawGroup[] = bld.buildingGroups.map((g) => ({
    name: g.name,
    title: g.title,
    buildings: g.buildings,
  }));
  if (game.ironWill && (game.libraryTab as { visible?: boolean } | null)?.visible) {
    groups.unshift({ name: "iw", title: "IW", buildings: [] });
  }
  groups.unshift({ name: "togglable", title: t("ui.filter.togglable"), buildings: [] });
  groups.unshift({ name: "allEnabled", title: t("ui.filter.enabled"), buildings: [] });
  groups.unshift({ name: "available", title: t("ui.filter.available"), buildings: [] });
  groups.unshift({ name: "all", title: t("ui.filter.all"), buildings: [] });
  return groups;
}

export function computeBonfireSnapshot(game: GamePage | null): BonfireSnapshot | null {
  if (!game) {
    return null;
  }
  const bld = game.bld as BldLike;
  const rawGroups = buildGroups(game);

  // buildings.js:3356-3366：组内任一 unlocked，或空组恒可见
  const groups: BonfireGroupEntry[] = rawGroups.map((g) => ({
    name: g.name,
    title: g.title,
    visible:
      g.buildings.length === 0 || g.buildings.some((name) => bld.get(name).unlocked),
  }));

  // buildings.js:3350-3352：activeGroup 存 tab 实例，默认 groups[0].name（"all"）
  const bldTab = game.bldTab as { activeGroup?: string } | null;
  const activeGroup = bldTab?.activeGroup || rawGroups[0].name;
  const twoRows = activeGroup === "all" || activeGroup === "iw";

  // 过滤求值需要 fetchModel 的组（available/allEnabled/togglable）；
  // 用一次性 controller（无状态，仅持 game 引用），不干扰按钮自身的订阅
  const needModelFilter =
    activeGroup === "available" || activeGroup === "allEnabled" || activeGroup === "togglable";
  let evalModern: ModernButtonController | null = null;
  let evalStaging: ModernButtonController | null = null;
  if (needModelFilter) {
    const ctors = window.classes?.ui?.btn;
    evalModern = ctors?.BuildingBtnModernController
      ? new ctors.BuildingBtnModernController(game)
      : null;
    evalStaging = ctors?.StagingBldBtnController
      ? new ctors.StagingBldBtnController(game)
      : null;
  }

  // buildings.js:3418-3497
  const buttons: BonfireButtonDesc[] = [];
  for (let i = 0; i < rawGroups.length; i++) {
    if (rawGroups[i].name !== activeGroup && !FILTER_GROUPS.includes(activeGroup)) {
      continue;
    }
    if (i === 0) {
      buttons.push({ kind: "gather" }, { kind: "refine" });
    }
    for (const bldName of rawGroups[i].buildings) {
      const meta = bld.getBuildingExt(bldName).getMeta();
      const staging = typeof meta.stages === "object" && meta.stages !== null;
      if (needModelFilter) {
        const controller = staging ? evalStaging : evalModern;
        if (!controller) {
          continue;
        }
        const mdl = controller.fetchModel({
          name: meta.label ?? bldName,
          description: meta.description ?? "",
          building: bldName,
          type: "buildings",
          twoRow: twoRows,
        });
        if (activeGroup === "available" && mdl.resourceIsLimited) {
          continue;
        }
        if (activeGroup === "allEnabled" && !mdl.enabled) {
          continue;
        }
        if (activeGroup === "togglable" && !mdl.togglable) {
          continue;
        }
        if (!mdl.visible) {
          continue;
        }
      } else {
        // all/iw：visible = unlocked || devMode（core.js:1886-1888），
        // iw 组跳过 population（buildings.js:3478-3482）
        if (activeGroup === "iw" && rawGroups[i].name === "population") {
          continue;
        }
        if (!bld.get(bldName).unlocked && !game.devMode) {
          continue;
        }
      }
      buttons.push({ kind: "bld", name: bldName, staging });
    }
  }

  return { activeGroup, twoRows, groups, buttons };
}

function snapshotEq(a: BonfireSnapshot | null, b: BonfireSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

function renderButton(desc: BonfireButtonDesc, twoRows: boolean) {
  if (desc.kind === "gather") {
    return <GatherCatnipButton key="gather" twoRow={twoRows} />;
  }
  if (desc.kind === "refine") {
    return <RefineCatnipButton key="refine" twoRow={twoRows} />;
  }
  return (
    <BuildingBtn
      key={desc.name}
      building={desc.name!}
      staging={desc.staging}
      twoRow={twoRows}
    />
  );
}

export default function BonfireTab() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeBonfireSnapshot(game), []),
    snapshotEq,
  );

  const onGroupClick = useCallback((groupName: string) => {
    const game = getGame() as (GamePage & { render?: () => void }) | null;
    if (!game) {
      return;
    }
    // buildings.js:3395-3398：activeGroup 存 tab 实例 + game.render()
    const bldTab = game.bldTab as { activeGroup?: string } | null;
    if (bldTab) {
      bldTab.activeGroup = groupName;
    }
    game.render?.();
  }, []);

  const content = useMemo(() => {
    if (!snap) {
      return null;
    }
    if (!snap.twoRows) {
      return snap.buttons.map((desc) => renderButton(desc, false));
    }
    // core.js:2449-2459：先收集可见按钮再按索引奇偶分列（INVENTORY 风险点 5）
    const left: React.ReactNode[] = [];
    const right: React.ReactNode[] = [];
    snap.buttons.forEach((desc, i) => {
      (i % 2 === 0 ? left : right).push(renderButton(desc, true));
    });
    return (
      <table cellPadding={0} cellSpacing={0} style={{ width: "100%" }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "top" }}>{left}</td>
            <td style={{ verticalAlign: "top" }}>{right}</td>
          </tr>
        </tbody>
      </table>
    );
  }, [snap]);

  if (!snap) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Bonfire">
        <div className="bldTopContainer">
          {snap.groups.map((group, i) => (
            <Fragment key={group.name}>
              {i !== 0 && (
                <span style={{ display: group.visible ? "" : "none" }}>{" · "}</span>
              )}
              <a
                href="#"
                style={{
                  display: group.visible ? "" : "none",
                  whiteSpace: "nowrap",
                }}
                className={group.name === snap.activeGroup ? "activeTab" : ""}
                onClick={() => onGroupClick(group.name)}
              >
                {group.title}
              </a>
            </Fragment>
          ))}
          <div className="bldGroupContainer">{content}</div>
        </div>
      </div>
    </TooltipProvider>
  );
}
